import { useMemo } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Icon,
  Link,
  Text,
  Title,
} from '@nimbus-ds/components';
import { EditIcon, PlusCircleIcon } from '@nimbus-ds/icons';
import {
  CurrencyType,
  FulfillmentOrdersResponseDto,
  OrderDetailsAmountsDto,
} from '@tiendanube/common';
import {
  FulfillmentPreferenceType,
  Status as StatusEnum,
} from '@tiendanube/common/src/enums';
import { CurrencyPrice } from 'commons/components';
import { useModal } from 'commons/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { DiscountEditModal } from './DiscountEditModal';
import { SummaryMessage } from './SummaryMessage';
import { SummaryBreakdown } from './SummarySubtotal';
import { useEditOrderDiscountActions, useShippingCosts } from '../../hooks';
import { FulfillmentsSummaryItem } from '../FulfillmentsSummary/FulfillmentsSummaryItem';
import { OrderEditDiscount, OrderEditProduct } from '../Products';

interface EditOrderSummaryProps {
  readonly orderAmounts: OrderDetailsAmountsDto;
  readonly products: OrderEditProduct[];
  readonly currency: CurrencyType;
  readonly isInsufficientStock?: boolean;
  readonly discounts: OrderEditDiscount[];
  readonly setDiscounts: (newDiscounts: OrderEditDiscount[]) => void;
  readonly isOrderEdited?: boolean;
  readonly isOrderDiscountsEdited?: boolean;
  readonly setIsOrderDiscountsEdited: (isEdited: boolean) => void;
  readonly notifyCustomer: boolean;
  readonly handleNotifyCustomer: () => void;
  readonly paymentStatus: StatusEnum;
  readonly fulfillments?: FulfillmentOrdersResponseDto[];
}

function EditOrderSummary({
  orderAmounts,
  products,
  currency,
  isInsufficientStock,
  discounts,
  setDiscounts,
  isOrderEdited,
  isOrderDiscountsEdited,
  setIsOrderDiscountsEdited,
  notifyCustomer,
  handleNotifyCustomer,
  paymentStatus,
  fulfillments,
}: EditOrderSummaryProps) {
  const t = useTranslationOrders();
  const [showDiscountsModal, openDiscountsModal, closeDiscountsModal] =
    useModal();
  const isDiscountsModalAllowed = useMemo(
    () =>
      fulfillments?.every(
        (ffoo) => ffoo.status.toLowerCase() === StatusEnum.UNPACKED,
      ),
    [fulfillments],
  );
  const { handleUpdateDiscounts } = useEditOrderDiscountActions(
    discounts,
    setDiscounts,
    isOrderDiscountsEdited || false,
    setIsOrderDiscountsEdited,
  );
  const {
    allFulfillmentsShippingCosts: shippingCosts,
    allFulfillmentsShippingCostsStatus: shippingCostsStatus,
  } = useShippingCosts(undefined);
  const firstFailedShippingCost = useMemo(
    () => shippingCostsStatus.find((sc) => sc.status === 'error')?.ffooId,
    [shippingCostsStatus],
  );

  const fulfillmentsProducts = useMemo(
    () =>
      fulfillments?.map((ffoo) => ({
        ffooId: ffoo.id,
        ffooName: ffoo.assignedLocation.name,
        ffooProducts: products.filter(
          (product) => product.fulfillmentOrderId === ffoo.id,
        ),
      })) ?? [],
    [products, fulfillments],
  );

  const fulfillmentWithoutProducts = useMemo(
    () =>
      fulfillmentsProducts.find((f) => !f.ffooProducts.length)?.ffooName ??
      (!products.length ? '' : undefined),
    [products, fulfillmentsProducts],
  );

  const shippableFulfillments = useMemo(
    () =>
      fulfillments?.filter(
        (ffoo) =>
          ![
            FulfillmentPreferenceType.DELIVERY_DIGITAL,
            FulfillmentPreferenceType.NON_SHIPPABLE,
          ].includes(ffoo.shipping.type),
      ),
    [fulfillments],
  );

  const failedFulfillmentCotization = useMemo(
    () =>
      fulfillments?.find((f) => f.id === firstFailedShippingCost)
        ?.assignedLocation.name ?? firstFailedShippingCost,
    [firstFailedShippingCost, fulfillments],
  );

  const newSubtotal = useMemo(
    () => products.reduce((acc, product) => acc + product.totalPrice, 0),
    [products],
  );

  const totalProducts = useMemo(
    () => products.reduce((acc, product) => acc + product.quantity, 0),
    [products],
  );

  const totalPaid = orderAmounts.paidByCustomer;

  const newShippingCost = useMemo(() => {
    if (!fulfillments) return orderAmounts.shipping ?? 0;
    return fulfillments?.reduce((acc, ffo) => {
      const newFFOCost = shippingCosts.find(
        (sc) => sc.fulfillmentOrderId === ffo.id,
      );
      const costValue =
        newFFOCost?.costs.merchantCost.amount ??
        ffo.shipping.merchantCost.value;
      acc += costValue;
      return acc;
    }, 0);
  }, [fulfillments, shippingCosts, orderAmounts]);

  const totalDiscount = useMemo(
    () =>
      discounts
        .filter((d) => d.editType !== 'remove')
        .reduce((acc, discount) => acc + discount.value, 0),
    [discounts],
  );

  const newTotal = useMemo(() => {
    const newTotalWithoutDiscounts = newSubtotal + newShippingCost;
    return newTotalWithoutDiscounts >= totalDiscount
      ? newTotalWithoutDiscounts - totalDiscount
      : 0;
  }, [newShippingCost, totalDiscount, newSubtotal]);

  const newTotalWithoutDiscounts = useMemo(
    () => newSubtotal + newShippingCost,
    [newShippingCost, newSubtotal],
  );

  return (
    <Card padding="none">
      <Box paddingX="4" paddingY="2">
        <Box paddingX="2" paddingBottom="2">
          <Title fontSize="5">{t('editOrders.summary.title')}</Title>
          <Box
            display="flex"
            flexDirection="row"
            paddingTop="1"
            paddingBottom="1"
            justifyContent="space-between"
          >
            <Box flex="2" alignItems="center" justifyContent="space-between">
              <Text color="neutral-textHigh">
                {t(`editOrders.summary.subtotal`, { count: totalProducts })}
              </Text>
            </Box>
            <CurrencyPrice
              price={newSubtotal}
              currency={currency}
              size="base"
            />
          </Box>

          {shippableFulfillments ? (
            shippableFulfillments.map((ffo: FulfillmentOrdersResponseDto) => (
              <FulfillmentsSummaryItem
                key={ffo.id}
                fulfillmentOrder={ffo}
                fulfillmentProducts={
                  fulfillmentsProducts.find((f) => f.ffooId === ffo.id)
                    ?.ffooProducts ?? []
                }
                totalShippingCost={orderAmounts.shipping ?? 0}
                currency={currency}
              />
            ))
          ) : (
            <FulfillmentsSummaryItem
              totalShippingCost={orderAmounts.shipping ?? 0}
              fulfillmentProducts={products}
              currency={currency}
            />
          )}
        </Box>

        <SummaryBreakdown
          discounts={discounts.filter((d) => d.editType !== 'remove')}
          currency={currency}
        />

        {isDiscountsModalAllowed && (
          <Box
            paddingBottom="2"
            paddingLeft="2"
            display="flex"
            flexDirection="row"
            gap="2-5"
          >
            <Link
              appearance="primary"
              textDecoration="none"
              onClick={() => {
                openDiscountsModal();
              }}
            >
              <Icon
                color="primary-interactive"
                source={
                  discounts.length > 0 ? <EditIcon /> : <PlusCircleIcon />
                }
              />
              {discounts.length > 0
                ? t('editOrders.summary.editDiscount')
                : t('editOrders.summary.addDiscount')}
            </Link>
          </Box>
        )}

        <Box
          paddingTop="2"
          paddingBottom="2"
          borderTopWidth="1"
          borderStyle="solid"
          borderColor="neutral-surfaceHighlight"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            paddingY="1"
            paddingX="2"
          >
            <Text color="neutral-textHigh" fontWeight="bold">
              {newTotal === orderAmounts.total
                ? t('editOrders.summary.total')
                : t('editOrders.summary.updatedTotal')}
            </Text>
            <CurrencyPrice price={newTotal} currency={currency} bold />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            paddingTop="1"
            paddingX="2"
          >
            <Text color="neutral-textHigh">{t('editOrders.summary.paid')}</Text>
            <CurrencyPrice price={totalPaid} currency={currency} />
          </Box>
          {isOrderEdited && paymentStatus !== StatusEnum.PENDING && (
            <Box paddingTop="2">
              <SummaryMessage
                totalPaid={totalPaid}
                newTotal={newTotal}
                currency={currency}
                isInsufficientStock={isInsufficientStock}
                fulfillmentWithoutProducts={fulfillmentWithoutProducts}
                failedFulfillmentCotization={failedFulfillmentCotization}
              />
            </Box>
          )}
          {isOrderEdited && (
            <Box paddingTop="4" paddingX="2">
              <Checkbox
                label={t('editOrders.sendNotification')}
                name="sendNotification"
                checked={notifyCustomer}
                onChange={handleNotifyCustomer}
                disabled={!isOrderEdited}
              />
            </Box>
          )}
          <DiscountEditModal
            show={showDiscountsModal}
            closeModal={closeDiscountsModal}
            discounts={discounts}
            updateDiscounts={handleUpdateDiscounts}
            newTotalWithoutDiscounts={newTotalWithoutDiscounts}
            currency={currency}
            total={newTotalWithoutDiscounts}
          />
        </Box>
      </Box>
    </Card>
  );
}

export default EditOrderSummary;
